/**
 * useProducts — ViewModel for /products/list.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for reads; owns CRUD mutations.
 *
 * Mutations send `multipart/form-data` (product JSON + media files +
 * per-file metadata) so the operator can upload images alongside the
 * product row in one request. Per-table dirty tracking (locked
 * decision 25 of the product-media design): when nothing changed on
 * the product side, the `product` part is omitted; when nothing
 * changed on the media side, the file/JSON media parts are omitted.
 *
 * The list response carries `categoryTree` extras — each category
 * carries its attributes inline, and 'custom' attributes carry the
 * linked custom_data_type's value list. The form modal uses this tree
 * to render the dynamic per-attribute inputs without a follow-up fetch.
 */

import { useCallback } from 'react'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  CategoryTreeNode,
  ProductAttributeCustomValueDto,
  ProductAttributeValueDto,
  ProductDto
} from '../models/product.model'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/product.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
//
// `ProductMediaDto` is the legacy viewmodel name; the model uses
// `ProductMediaListEntry` for the same shape (avoids a media.model
// import cycle on the model side). Re-export as both names so any
// existing consumer that imports `ProductMediaDto` from this hook
// continues to compile without a sweep.
export type {
  CategoryTreeAttribute,
  CategoryTreeNode,
  ProductAttributeCustomValueDto,
  ProductAttributeValueDto,
  ProductDto,
  ProductMediaListEntry as ProductMediaDto
} from '../models/product.model'

/** Per-file metadata client builds + ships in media_metadata / media_new_metadata. */
export interface MediaSlotMetadata {
  readonly alt_text: string | null
  readonly sort_order: number
  readonly is_thumbnail: boolean
  /** Browser-extracted (decision 22) — required for images, null for video. */
  readonly width: number | null
  readonly height: number | null
  readonly original_name: string | null
}

export interface NewMediaSlot {
  readonly file: File
  readonly metadata: MediaSlotMetadata
}

/** Per-link update applied to an EXISTING product_media row. */
export interface MediaLinkUpdate {
  readonly link_id: number
  readonly alt_text?: string | null
  readonly sort_order?: number
  readonly is_thumbnail?: boolean
}

/** POST /products multipart shape (product part + ≥1 file). */
export interface CreateProductInput {
  readonly product: {
    readonly name: string
    readonly description: string
    readonly category_id: string
    readonly max_order_quantity: number | null
    readonly display_in_catalog: boolean
    readonly attribute_values: ProductAttributeValueDto[]
    readonly attribute_custom_values: ProductAttributeCustomValueDto[]
  }
  readonly media: NewMediaSlot[]
}

/** PATCH /products/:id multipart shape — every section optional. */
export interface UpdateProductInput {
  readonly product?: {
    readonly name?: string
    readonly description?: string
    readonly category_id?: string
    readonly max_order_quantity?: number | null
    readonly display_in_catalog?: boolean
    readonly attribute_values?: ProductAttributeValueDto[]
    readonly attribute_custom_values?: ProductAttributeCustomValueDto[]
  }
  readonly mediaNew?: NewMediaSlot[]
  readonly mediaUpdates?: MediaLinkUpdate[]
  readonly mediaDeletes?: number[]
}

export interface UseProductsReturn {
  readonly items: ProductDto[]
  readonly categoryTree: CategoryTreeNode[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly create: (
    input: CreateProductInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly update: (
    id: string,
    patch: UpdateProductInput
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly decommission: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  /**
   * Hard delete via DELETE /products/:id. Gated server-side on
   * active_variant_count === 0 — propagated products cannot be
   * deleted (the server returns 409). Coexists with decommission:
   * Delete is for never-propagated products; Decommission for
   * "stop selling, keep history". Per Kiran 2026-05-19.
   */
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
  /**
   * Reconcile variants against the product's saved CDT selections.
   * Idempotent. The server inserts new combos, restores soft-deleted
   * matches, and soft-deletes active combos no longer in the
   * selection set. Returns the updated ProductDto so the list row
   * (and the open modal) picks up the new active_variant_count +
   * propagated_axes_selections.
   */
  readonly propagateVariants: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: ProductDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
    readonly categoryTree: CategoryTreeNode[]
  }
}

interface ItemBody {
  readonly success: true
  readonly data: ProductDto
}

function buildCreateFormData(input: CreateProductInput): FormData {
  const form = new FormData()
  // JSON parts go as strings — `formData.get()` only returns a string
  // when the entry was appended as one. A Blob (even with type
  // 'application/json') is returned as a File-like and the worker-side
  // `getStringPart` rejects it.
  form.append('product', JSON.stringify(input.product))
  const metadataList = input.media.map((m) => m.metadata)
  for (const slot of input.media) {
    form.append('media', slot.file, slot.metadata.original_name ?? slot.file.name)
  }
  form.append('media_metadata', JSON.stringify(metadataList))
  return form
}

function buildUpdateFormData(input: UpdateProductInput): FormData {
  const form = new FormData()
  if (input.product !== undefined) {
    form.append('product', JSON.stringify(input.product))
  }
  if (input.mediaNew !== undefined && input.mediaNew.length > 0) {
    const metadataList = input.mediaNew.map((m) => m.metadata)
    for (const slot of input.mediaNew) {
      form.append('media_new', slot.file, slot.metadata.original_name ?? slot.file.name)
    }
    form.append('media_new_metadata', JSON.stringify(metadataList))
  }
  if (input.mediaUpdates !== undefined && input.mediaUpdates.length > 0) {
    form.append('media_updates', JSON.stringify(input.mediaUpdates))
  }
  if (input.mediaDeletes !== undefined && input.mediaDeletes.length > 0) {
    form.append('media_deletes', JSON.stringify(input.mediaDeletes))
  }
  return form
}

/**
 * @param categoryId Optional category filter. `null` = show all
 *   categories. Changing it resets pagination to page 1 (handled via
 *   usePaginatedList's `deps` mechanism). Caller (the page component)
 *   owns the state so other UI bits — like a "Showing X of Y" caption
 *   or a filter chip — can read it without re-wiring through here.
 */
export function useProducts(categoryId: string | null = null): UseProductsReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<ProductDto, CategoryTreeNode[]>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
        if (categoryId !== null) params.set('categoryId', categoryId)
        const url = `${APIROUTES.PRODUCTS}?${params.toString()}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total,
          extras: body.data.categoryTree
        }
      },
      [authedFetch, categoryId]
    ),
    // Filter dep → page snaps back to 1 BEFORE the fetch effect fires.
    deps: [categoryId]
  })

  // Products mutations are MULTIPART (product JSON + binary media
  // files + per-file metadata) so mutateJson — which assumes JSON
  // bodies — doesn't fit. We still use the response envelope + the
  // primitives; only the request shape diverges. parseApiError stays
  // the source of truth for the error path.
  const create = useCallback(
    async (input: CreateProductInput) => {
      const res = await authedFetch(APIROUTES.PRODUCTS, {
        method: 'POST',
        body: buildCreateFormData(input)
      })
      if (!res.ok) {
        const e = await parseApiError(res)
        return { ok: false as const, field: e.field, message: e.message }
      }
      const body = (await res.json()) as ItemBody
      if (paged.page === 1) paged.insertItem(body.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateProductInput) => {
      const res = await authedFetch(fillPath(APIROUTES.PRODUCTITEM, { id }), {
        method: 'PATCH',
        body: buildUpdateFormData(patch)
      })
      if (!res.ok) {
        const e = await parseApiError(res)
        return { ok: false as const, field: e.field, message: e.message }
      }
      const body = (await res.json()) as ItemBody
      paged.updateItem((it) => it.id === id, body.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  // Operators decommission propagated products. The server flips
  // is_decommissioned=1 and returns the updated row; we update the
  // list in place rather than removing it so the row stays visible
  // with a "Decommissioned" badge.
  const decommission = useCallback(
    async (id: string) => {
      const r = await mutateJson<ProductDto>(
        authedFetch,
        fillPath(APIROUTES.PRODUCTDECOMMISSION, { id }),
        'POST'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  // Hard delete — only allowed when no active variants exist. The
  // server returns 409 when the product has been propagated; the UI
  // surfaces that as an error and the button on the list is disabled
  // proactively (row.active_variant_count gate).
  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.PRODUCTITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  // Propagate variants — the operator clicks the Propagate button
  // inside the Edit modal. Server reconciles based on the product's
  // SAVED CDT selections (operator must Save form changes first).
  const propagateVariants = useCallback(
    async (id: string) => {
      const r = await mutateJson<ProductDto>(
        authedFetch,
        fillPath(APIROUTES.PRODUCTVARIANTSPROPAGATE, { id }),
        'POST'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    categoryTree: paged.extras ?? [],
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    create,
    update,
    decommission,
    remove,
    propagateVariants
  }
}
